import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import templateService from '../services/templateService';
import { saveAs } from 'file-saver';
import Header from './Header';
import Footer from './Footer';
import ProductSidebar from './ProductSidebar';
import DashboardOverview from './DashboardOverview';
import { createDoc, PDF, fR, sR, T, drawCoverHeader, drawPageHeader, drawFooters, drawSectionHeading } from '../utils/pdfBrand';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useUser();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  const [interviewQuestionsHistory, setInterviewQuestionsHistory] = useState([]);
  const [expandedInterviewSessions, setExpandedInterviewSessions] = useState(new Set());
  const [loadingSessionDetails, setLoadingSessionDetails] = useState({});
  const [coverLetterHistory, setCoverLetterHistory] = useState([]);
  const [expandedInterviewAssistantSessions, setExpandedInterviewAssistantSessions] = useState(null);
  const interviewSessionRefsRef = useRef({});
  const [loadingInterviewAssistantDetails, setLoadingInterviewAssistantDetails] = useState({});
  const [autoApplySessions, setAutoApplySessions] = useState([]);
  const [allAppliedJobs, setAllAppliedJobs] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null); // only one open at a time
  const [sessionJobs, setSessionJobs] = useState({}); // keyed by sessionId
  const [loadingSessionJobs, setLoadingSessionJobs] = useState({});
  
  const [mockInterviewHistory, setMockInterviewHistory] = useState([]);
  const [expandedMockInterviews, setExpandedMockInterviews] = useState(new Set());
  const [loadingMockInterviewDetails, setLoadingMockInterviewDetails] = useState({});

  const [expandedCoverLetters, setExpandedCoverLetters] = useState(new Set());
  const [loadingCoverLetterDetails, setLoadingCoverLetterDetails] = useState({});
  const [coverLetterDetails, setCoverLetterDetails] = useState({});

  const [expandedResumes, setExpandedResumes] = useState(new Set());
  const [loadingResumeDetails, setLoadingResumeDetails] = useState({});
  const [resumeDetails, setResumeDetails] = useState({});

  const [stats, setStats] = useState({
    totalInterviews: 0,
    resumesBuilt: 0,
    coverLetters: 0,
    interviewQuestions: 0,
    mockInterviews: 0,
  });

  const [userHistory, setUserHistory] = useState({
    interviews: [],
    resumesBuilt: [],
    coverLetters: [],
    interviewQuestions: [],
    quizzes: []
  });

  const [resumeHistory, setResumeHistory] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    const fetchResumeHistory = async () => {
      if (!user) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/resume/history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setResumeHistory(data.resumes);
          
          setStats(prev => ({
            ...prev,
            resumesBuilt: data.resumes.length
          }));
        }
      } catch (error) {
        console.error('Error fetching resume history:', error);
      }
    };

    fetchResumeHistory();
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchInterviewQuestionsHistory = async () => {
      if (!user) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/interview-questions/history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setInterviewQuestionsHistory(data.sessions);
          
          setStats(prev => ({
            ...prev,
            interviewQuestions: data.sessions.length
          }));
        }
      } catch (error) {
        console.error('Error fetching interview questions history:', error);
      }
    };

    fetchInterviewQuestionsHistory();
  }, [user, API_BASE_URL]);

  useEffect(() => {
    const fetchCoverLetterHistory = async () => {
      if (!user) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/cover-letter/history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setCoverLetterHistory(data.coverLetters);
          
          setStats(prev => ({
            ...prev,
            coverLetters: data.coverLetters.length
          }));
        }
      } catch (error) {
        console.error('Error fetching cover letter history:', error);
      }
    };

    fetchCoverLetterHistory();
  }, [user, API_BASE_URL]);

    useEffect(() => {
    const fetchInterviewAssistantHistory = async () => {
        if (!user) return;
        
        try {
        const response = await fetch(`${API_BASE_URL}/api/interview-assistant/history`, {
            headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        
        if (response.ok) {
            const data = await response.json();
            setUserHistory(prev => ({
            ...prev,
            interviews: data.sessions
            }));
            
            setStats(prev => ({
            ...prev,
            totalInterviews: data.sessions.length
            }));
        }
        } catch (error) {
        console.error('Error fetching interview assistant history:', error);
        }
    };

    fetchInterviewAssistantHistory();
    }, [user, API_BASE_URL]);

  const toggleInterviewSession = async (sessionId) => {
    const newExpanded = new Set();
    
    if (expandedInterviewSessions.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
      
      const session = interviewQuestionsHistory.find(s => s.id === sessionId);
      if (!session.detailsLoaded) {
        await fetchInterviewSessionDetails(sessionId);
      }
    }
    
    setExpandedInterviewSessions(newExpanded);
  };

const toggleInterviewAssistantSession = async (sessionId) => {
    if (expandedInterviewAssistantSessions === sessionId) {
        setExpandedInterviewAssistantSessions(null);
        return;
    }
    setExpandedInterviewAssistantSessions(sessionId);
    const session = userHistory.interviews.find(s => s.id === sessionId);
    if (!session.detailsLoaded) {
        await fetchInterviewAssistantSessionDetails(sessionId);
    }
    setTimeout(() => {
        const el = interviewSessionRefsRef.current[sessionId];
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
          window.scrollTo({ top, behavior: 'smooth' });
        }
    }, 100);
};

  const fetchInterviewSessionDetails = async (sessionId) => {
    setLoadingSessionDetails(prev => ({ ...prev, [sessionId]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview-questions/history/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const updatedSession = { ...interviewQuestionsHistory.find(s => s.id === sessionId), ...data.session, detailsLoaded: true };
        
        setInterviewQuestionsHistory(prev => prev.map(s => 
          s.id === sessionId 
            ? updatedSession
            : s
        ));
        
        return updatedSession;
      }
    } catch (error) {
      console.error('Error fetching session details:', error);
    } finally {
      setLoadingSessionDetails(prev => ({ ...prev, [sessionId]: false }));
    }
    return null;
  };

    const fetchInterviewAssistantSessionDetails = async (sessionId) => {
        setLoadingInterviewAssistantDetails(prev => ({ ...prev, [sessionId]: true }));
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/interview-assistant/history/${sessionId}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
            });
            
            if (response.ok) {
            const data = await response.json();
            const updatedSession = { 
                ...userHistory.interviews.find(s => s.id === sessionId), 
                ...data.session, 
                detailsLoaded: true 
            };
            
            setUserHistory(prev => ({
                ...prev,
                interviews: prev.interviews.map(s => 
                s.id === sessionId ? updatedSession : s
                )
            }));
            
            return updatedSession;
            }
        } catch (error) {
            console.error('Error fetching session details:', error);
        } finally {
            setLoadingInterviewAssistantDetails(prev => ({ ...prev, [sessionId]: false }));
        }
        return null;
    };

    useEffect(() => {
    const fetchRecentActivity = async () => {
        if (!user) return;
        
        try {
        console.log('Fetching recent activity...');
        const response = await fetch(`${API_BASE_URL}/api/recent-activity`, {
            headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        console.log('Response status:', response.status);
        
        if (response.ok) {
            const data = await response.json();
            console.log('Recent activities received:', data.activities);
            setRecentActivities(data.activities);
        }
        } catch (error) {
        console.error('Error fetching recent activity:', error);
        }
    };

    fetchRecentActivity();
    }, [user, API_BASE_URL]);

    useEffect(() => {
    const fetchMockInterviewHistory = async () => {
      if (!user) return;
      try {
        const response = await fetch(`${API_BASE_URL}/api/mock-interview/history`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setMockInterviewHistory(data.sessions || []);
          setStats(prev => ({ ...prev, mockInterviews: (data.sessions || []).length }));
        }
      } catch (error) {
        console.error('Error fetching mock interview history:', error);
      }
    };
    fetchMockInterviewHistory();
    }, [user, API_BASE_URL]);

    useEffect(() => {
      const fetchAutoApplySessions = async () => {
        if (!user) return;
        try {
          const res = await fetch(`${API_BASE_URL}/api/auto-apply/history`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (res.ok) {
            const data = await res.json();
            setAutoApplySessions(data.sessions || []);
          }
        } catch (err) {
          console.error('Error fetching auto apply sessions:', err);
        }
      };
      fetchAutoApplySessions();
    }, [user, API_BASE_URL]);

    useEffect(() => {
      const fetchAllAppliedJobs = async () => {
        if (!user) return;
        try {
          const res = await fetch(`${API_BASE_URL}/api/auto-apply/all-jobs`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (res.ok) {
            const data = await res.json();
            setAllAppliedJobs(data.jobs || []);
          }
        } catch (err) {
          console.error('Error fetching all applied jobs:', err);
        }
      };
      fetchAllAppliedJobs();
    }, [user, API_BASE_URL]);

    const toggleSession = async (sessionId) => {
      // If clicking the already open one, close it
      if (expandedSession === sessionId) {
        setExpandedSession(null);
        return;
      }

      setExpandedSession(sessionId);

      // Fetch jobs for this session if not already loaded
      if (!sessionJobs[sessionId]) {
        setLoadingSessionJobs(prev => ({ ...prev, [sessionId]: true }));
        try {
          const res = await fetch(`${API_BASE_URL}/api/auto-apply/history/${sessionId}/jobs`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (res.ok) {
            const data = await res.json();
            setSessionJobs(prev => ({ ...prev, [sessionId]: data.jobs || [] }));
          }
        } catch (err) {
          console.error('Error fetching session jobs:', err);
        } finally {
          setLoadingSessionJobs(prev => ({ ...prev, [sessionId]: false }));
        }
      }
    };

    const renderAutoApplySessions = () => (
      <div className="history-section">
        <div className="section-header">
          <h2>Auto Apply Sessions ({autoApplySessions.length})</h2>
          <button className="action-btn" onClick={() => navigate('/AutoJobApply')}>
            Start New Session
          </button>
        </div>

        {autoApplySessions.length === 0 ? (
          <div className="empty-state glass">
            <div className="empty-icon">🚀</div>
            <h3>No Auto Apply Sessions Yet</h3>
            <p>Launch the Auto Job Apply agent to start applying to jobs automatically.</p>
            <button className="cta-button" onClick={() => navigate('/AutoJobApply')}>
              Start Auto Apply
            </button>
          </div>
        ) : (
          <div className="quiz-history-list">
            {autoApplySessions.map((session) => {
              const prefs = typeof session.preferences_data === 'string'
                ? JSON.parse(session.preferences_data)
                : session.preferences_data;
              const stats = typeof session.stats_data === 'string'
                ? JSON.parse(session.stats_data)
                : session.stats_data;
              const isOpen = expandedSession === session.id;
              const jobs = sessionJobs[session.id] || [];

              return (
                <div key={session.id} className="quiz-history-item glass">
                  <div className="quiz-header" style={{ cursor: 'pointer' }} onClick={() => toggleSession(session.id)}>
                    <div className="quiz-header-left">
                      <div className="quiz-info">
                        <div className="quiz-card-top">
                          <h4 className="quiz-card-title" style={{ textTransform: 'capitalize' }}>
                            {prefs?.jobTitles || 'Session'}{prefs?.locations ? <span className="quiz-card-company"> · {prefs.locations}</span> : ''}
                          </h4>
                          <span className="quiz-level-badge" style={{
                            color: session.status === 'completed' ? '#4ade80' : '#fbbf24',
                            background: session.status === 'completed' ? 'rgba(74,222,128,0.12)' : 'rgba(251,191,36,0.12)',
                            borderColor: session.status === 'completed' ? 'rgba(74,222,128,0.25)' : 'rgba(251,191,36,0.25)',
                          }}>
                            {session.status === 'completed' ? 'Completed' : 'Running'}
                          </span>
                        </div>
                        <div className="quiz-meta">
                          <span className="meta-item" style={{ color: '#4ade80' }}>{stats?.applied || 0} Applied</span>
                          <span className="meta-dot"/>
                          <span className="meta-item" style={{ color: '#f87171' }}>{stats?.failed || 0} Failed</span>
                          <span className="meta-dot"/>
                          <span className="meta-item">{stats?.total || 0} Total</span>
                          <span className="meta-dot"/>
                          <span className="meta-item">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            {new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="quiz-header-right">
                      <span className="expand-icon">{isOpen ? '−' : '+'}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="quiz-details" style={{ maxHeight: '400px', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {loadingSessionJobs[session.id] ? (
                        <div className="loading-details">Loading jobs...</div>
                      ) : jobs.length === 0 ? (
                        <div className="no-details">No jobs found for this session.</div>
                      ) : (
                        <table style={{
                          width: '100%', borderCollapse: 'collapse',
                          fontSize: '13px', color: '#ccc'
                        }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              {['#', 'Job Title', 'Company', 'Location', 'Platform', 'Status', 'Applied At'].map(h => (
                                <th key={h} style={{
                                  padding: '10px 12px', textAlign: 'left',
                                  color: '#e0a3ff', fontWeight: 600
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {jobs.map((job, i) => (
                              <tr key={job.id} style={{
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'
                              }}>
                                <td style={{ padding: '10px 12px' }}>{i + 1}</td>
                                <td style={{ padding: '10px 12px', color: '#fff' }}>{job.job_title}</td>
                                <td style={{ padding: '10px 12px' }}>{job.company || '—'}</td>
                                <td style={{ padding: '10px 12px' }}>{job.location || '—'}</td>
                                <td style={{ padding: '10px 12px' }}>
                                  <span style={{
                                    background: 'rgba(224,163,255,0.15)',
                                    padding: '2px 8px', borderRadius: '4px',
                                    color: '#e0a3ff', fontSize: '12px'
                                  }}>{job.platform}</span>
                                </td>
                                <td style={{ padding: '10px 12px' }}>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: '4px', fontSize: '12px',
                                    background: job.status === 'applied'
                                      ? 'rgba(74,222,128,0.15)'
                                      : job.status === 'failed'
                                      ? 'rgba(248,113,113,0.15)'
                                      : 'rgba(251,191,36,0.15)',
                                    color: job.status === 'applied' ? '#4ade80'
                                      : job.status === 'failed' ? '#f87171' : '#fbbf24'
                                  }}>
                                    {job.status}
                                  </span>
                                </td>
                                <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                                  {job.applied_at ? new Date(job.applied_at).toLocaleTimeString() : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );

const renderAllAppliedJobs = () => (
  <div className="history-section">
    <div className="section-header">
      <h2>All Applied Jobs ({allAppliedJobs.length})</h2>
    </div>

    {allAppliedJobs.length === 0 ? (
      <div className="empty-state glass">
        <div className="empty-icon">💼</div>
        <h3>No Jobs Applied Yet</h3>
        <p>Jobs applied through Auto Apply will appear here.</p>
        <button className="cta-button" onClick={() => navigate('/AutoJobApply')}>
          Start Auto Apply
        </button>
      </div>
    ) : (
      <div className="quiz-history-list glass" style={{ padding: '20px', borderRadius: '16px' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            fontSize: '13px', color: '#ccc'
          }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {['#', 'Job Title', 'Company', 'Location', 'Platform', 'Status', 'Applied At'].map(h => (
                  <th key={h} style={{
                    padding: '12px 14px', textAlign: 'left',
                    color: '#e0a3ff', fontWeight: 600, whiteSpace: 'nowrap'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allAppliedJobs.map((job, i) => (
                <tr key={job.id} style={{
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                  transition: 'background 0.2s'
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(224,163,255,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}
                >
                  <td style={{ padding: '12px 14px', color: '#999' }}>{i + 1}</td>
                  <td style={{ padding: '12px 14px', color: '#fff', fontWeight: 500 }}>{job.job_title}</td>
                  <td style={{ padding: '12px 14px' }}>{job.company || '—'}</td>
                  <td style={{ padding: '12px 14px' }}>{job.location || '—'}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      background: 'rgba(224,163,255,0.15)',
                      padding: '3px 10px', borderRadius: '4px',
                      color: '#e0a3ff', fontSize: '12px'
                    }}>{job.platform}</span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: '4px', fontSize: '12px',
                      background: job.status === 'applied'
                        ? 'rgba(74,222,128,0.15)'
                        : job.status === 'failed'
                        ? 'rgba(248,113,113,0.15)'
                        : 'rgba(251,191,36,0.15)',
                      color: job.status === 'applied' ? '#4ade80'
                        : job.status === 'failed' ? '#f87171' : '#fbbf24'
                    }}>
                      {job.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: '12px', color: '#999' }}>
                    {job.applied_at ? new Date(job.applied_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

  const handleGenerateInterviewQuestionsPDF = (session) => {
    if (!session.questions_data) return;

    const questions = typeof session.questions_data === 'string' 
      ? JSON.parse(session.questions_data) 
      : session.questions_data;

    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Interview Questions - ${session.job_title}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; color: #333; }
    h1 { color: #9370db; border-bottom: 3px solid #9370db; padding-bottom: 10px; }
    .metadata { background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0; }
    .metadata p { margin: 5px 0; }
    .question { margin: 30px 0; padding: 20px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid; background: #fafafa; }
    .question-header { font-weight: bold; font-size: 18px; margin-bottom: 15px; color: #9370db; }
    .answer { margin-top: 12px; padding: 15px; background: white; border-left: 3px solid #9370db; border-radius: 4px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Interview Questions & Answers</h1>
  <div class="metadata">
    <p><strong>Position:</strong> ${session.job_title}</p>
    ${session.company_name ? `<p><strong>Company:</strong> ${session.company_name}</p>` : ''}
    <p><strong>Total Questions:</strong> ${session.total_questions}</p>
    <p><strong>Generated:</strong> ${new Date(session.created_at).toLocaleDateString()}</p>
    ${session.resume_filename ? `<p><strong>Resume:</strong> ${session.resume_filename}</p>` : ''}
  </div>
  ${questions.map((q, idx) => `
    <div class="question">
      <div class="question-header">Q${idx + 1}. ${q.question}</div>
      <div class="answer">
        <strong>Answer:</strong><br><br>
        ${q.answer}
      </div>
    </div>
  `).join('')}
  <div class="footer">
    <p>Generated by TERVIEW Interview Question Generator</p>
    <p>${new Date().getFullYear()} All rights reserved</p>
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleDownloadCoverLetter = async (coverLetter) => {
    try {
      const { Document, Packer, Paragraph, TextRun, AlignmentType } = await import('docx');
      const { saveAs } = await import('file-saver');
      
      const docChildren = [];
      const lines = coverLetter.cover_letter_content.split('\n');
      
      let lineIndex = 0;
      
      // Process name (first non-empty line)
      while (lineIndex < lines.length && !lines[lineIndex].trim()) {
        lineIndex++;
      }
      
      if (lineIndex < lines.length) {
        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: lines[lineIndex].trim(),
                font: 'Times New Roman',
                size: 36,
                bold: true
              })
            ],
            spacing: { 
              after: 120,
              line: 360
            },
            alignment: AlignmentType.CENTER
          })
        );
        lineIndex++;
      }
      
      // Process contact info
      while (lineIndex < lines.length && !lines[lineIndex].trim()) {
        lineIndex++;
      }
      
      if (lineIndex < lines.length) {
        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: lines[lineIndex].trim(),
                font: 'Times New Roman',
                size: 24
              })
            ],
            spacing: { 
              after: 360,
              line: 360
            },
            alignment: AlignmentType.CENTER
          })
        );
        lineIndex++;
      }
      
      // Process body
      for (let i = lineIndex; i < lines.length; i++) {
        const trimmedLine = lines[i].trim();
        
        if (!trimmedLine) continue;
        
        if (trimmedLine.toLowerCase().startsWith('sincerely')) {
          docChildren.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: trimmedLine,
                  font: 'Times New Roman',
                  size: 24
                })
              ],
              spacing: { 
                before: 240,
                after: 0, 
                line: 360
              },
              alignment: AlignmentType.LEFT
            })
          );
          
          // Try to get name from personal_info
          let personalInfo;
          try {
            personalInfo = typeof coverLetter.personal_info === 'string' 
              ? JSON.parse(coverLetter.personal_info) 
              : coverLetter.personal_info;
          } catch (e) {
            console.error('Error parsing personal info:', e);
          }
          
          if (personalInfo?.fullName) {
            docChildren.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: personalInfo.fullName,
                    font: 'Times New Roman',
                    size: 24
                  })
                ],
                spacing: { 
                  before: 0, 
                  line: 360
                },
                alignment: AlignmentType.LEFT
              })
            );
          }
          break;
        }
        
        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: trimmedLine,
                font: 'Times New Roman',
                size: 24
              })
            ],
            spacing: { 
              after: 180,
              line: 360
            },
            alignment: AlignmentType.LEFT
          })
        );
      }

      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440
              }
            }
          },
          children: docChildren
        }]
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, coverLetter.file_name || 'Cover_Letter.docx');
    } catch (error) {
      console.error('Error downloading cover letter:', error);
      alert('Error downloading cover letter. Please try again.');
    }
  };

    const handleGenerateInterviewAssistantPDF = (session) => {
        if (!session.conversation_data) return;

        const conversation = typeof session.conversation_data === 'string' 
            ? JSON.parse(session.conversation_data) 
            : session.conversation_data;

        const durationMinutes = Math.floor(session.duration_seconds / 60);
        const durationSeconds = session.duration_seconds % 60;

        const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
        <meta charset="utf-8">
        <title>Interview Transcript - ${session.job_role}</title>
        <style>
            body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; color: #333; }
            h1 { color: #9370db; border-bottom: 3px solid #9370db; padding-bottom: 10px; }
            .metadata { background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0; }
            .metadata p { margin: 5px 0; }
            .conversation-item { margin: 30px 0; padding: 20px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid; }
            .conversation-item.interviewer { border-left: 4px solid #ff69b4; background: #fef5f9; }
            .conversation-item.ai { border-left: 4px solid #9370db; background: #f5f0ff; }
            .item-header { font-weight: bold; font-size: 16px; margin-bottom: 10px; color: #333; display: flex; justify-content: space-between; }
            .timestamp { color: #666; font-size: 14px; font-weight: normal; }
            .message-content { line-height: 1.8; white-space: pre-wrap; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px; }
        </style>
        </head>
        <body>
        <h1>Interview Practice Session</h1>
        <div class="metadata">
            <p><strong>Position:</strong> ${session.job_role}</p>
            ${session.company ? `<p><strong>Company:</strong> ${session.company}</p>` : ''}
            <p><strong>Experience Level:</strong> ${session.experience_level}</p>
            <p><strong>Date:</strong> ${new Date(session.created_at).toLocaleDateString()}</p>
            <p><strong>Duration:</strong> ${durationMinutes}:${durationSeconds.toString().padStart(2, '0')}</p>
            <p><strong>Total Exchanges:</strong> ${session.total_exchanges}</p>
            <p><strong>Status:</strong> ${session.completed_naturally ? 'Completed Naturally' : 'Manually Ended'}</p>
        </div>
        <h2>Conversation Transcript</h2>
        ${(() => {
          const rev = [...conversation].reverse();
          const pairs = [];
          for (let i = 0; i < rev.length; i += 2) {
            const a = rev[i], b = rev[i + 1];
            if (a && b && a.sender === 'ai' && b.sender === 'interviewer') { pairs.push(b); pairs.push(a); }
            else { if (a) pairs.push(a); if (b) pairs.push(b); }
          }
          return pairs;
        })().map((item) => `
            <div class="conversation-item ${item.sender}">
            <div class="item-header">
                <span>${item.sender === 'ai' ? 'Your Response' : 'Interviewer Question'}</span>
                <span class="timestamp">${item.timestamp}</span>
            </div>
            <div class="message-content">${item.message}</div>
            </div>
        `).join('')}
        <div class="footer">
            <p>Generated by TERVIEW Interview Assistant</p>
            <p>${new Date().getFullYear()} All rights reserved</p>
        </div>
        </body>
        </html>`;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
        };
    };

  if (loading) {
    return (
      <div className="neural-glass-homepage">
        <div className="neural-background"></div>
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#e0a3ff',
          fontSize: '1.2rem',
          position: 'relative',
          zIndex: 10
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

    // Map a tool label (as used in DashboardOverview's distribution) back to
    // the tab inside this dashboard so clicking a trend row jumps the user
    // straight to that tool's history.
    const TOOL_TO_TAB = {
      'Interview Assistant':  'interviews',
      'Mock Interview':       'mockInterviews',
      'Resume Builder':       'resumes',
      'Cover Letter':         'coverLetters',
      'Interview Questions':  'interviewQuestions',
    };

    const renderOverview = () => (
      <DashboardOverview
        user={user}
        stats={stats}
        recentActivities={recentActivities}
        autoApplySessions={autoApplySessions}
        allAppliedJobs={allAppliedJobs}
        onNavigate={(toolLabel) => {
          const tab = TOOL_TO_TAB[toolLabel];
          if (tab) setActiveTab(tab);
        }}
      />
    );

    const renderInterviews = () => (
        <div className="history-section">
        <div className="section-header">
        <h2>Interview Assistant History ({userHistory.interviews.length})</h2>
        <button className="action-btn" onClick={() => navigate('/InterviewAssistant')}>
            Start New Interview
        </button>
        </div>
        
        {userHistory.interviews.length === 0 ? (
        <div className="empty-state glass">
            <div className="empty-icon">🎯</div>
            <h3>No Interview Sessions Yet</h3>
            <p>Start your first AI-powered interview practice session to see your history here.</p>
            <button className="cta-button" onClick={() => navigate('/InterviewAssistant')}>
            Start Interview
            </button>
        </div>
        ) : (
        <div className="quiz-history-list">
            {userHistory.interviews.map((session) => {
            const durationMinutes = Math.floor(session.duration_seconds / 60);
            const durationSeconds = session.duration_seconds % 60;
            
            return (
                <div key={session.id} className="quiz-history-item glass" ref={el => interviewSessionRefsRef.current[session.id] = el}>
                <div className="quiz-header">
                    <div className="quiz-header-left" onClick={() => toggleInterviewAssistantSession(session.id)}>
                    <div className="quiz-info">
                        <div className="quiz-card-top">
                          <h4 className="quiz-card-title">{session.job_role}{session.company && <span className="quiz-card-company"> · {session.company}</span>}</h4>
                          <span className="quiz-level-badge">{session.experience_level} Level</span>
                        </div>
                        <div className="quiz-meta">
                          <span className="meta-item">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            {durationMinutes}:{durationSeconds.toString().padStart(2, '0')}
                          </span>
                          <span className="meta-dot"/>
                          <span className="meta-item">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            {new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                    </div>
                    </div>
                    <div className="quiz-header-right">
                    <button 
                        className="pdf-download-btn-improved"
                        onClick={async (e) => {
                        e.stopPropagation();
                        if (!session.detailsLoaded) {
                            const updatedSession = await fetchInterviewAssistantSessionDetails(session.id);
                            if (updatedSession && updatedSession.conversation_data) {
                            handleGenerateInterviewAssistantPDF(updatedSession);
                            }
                        } else {
                            handleGenerateInterviewAssistantPDF(session);
                        }
                        }}
                        title="Download PDF"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        <span>Download PDF</span>
                    </button>
                    <span className="expand-icon" onClick={() => toggleInterviewAssistantSession(session.id)}>
                        {expandedInterviewAssistantSessions === session.id ? '−' : '+'}
                    </span>
                    </div>
                </div>
                
                {expandedInterviewAssistantSessions === session.id && (
                    <div className="quiz-details">
                    {loadingInterviewAssistantDetails[session.id] ? (
                        <div className="loading-details">Loading conversation...</div>
                    ) : session.conversation_data ? (
                        <div className="interview-questions-review">
                        {(() => {
                            const conversation = typeof session.conversation_data === 'string' 
                            ? JSON.parse(session.conversation_data) 
                            : session.conversation_data;
                            
                            const rev = [...conversation].reverse();
                            const ordered = [];
                            for (let i = 0; i < rev.length; i += 2) {
                              const a = rev[i], b = rev[i + 1];
                              if (a && b && a.sender === 'ai' && b.sender === 'interviewer') {
                                ordered.push(b); ordered.push(a);
                              } else {
                                if (a) ordered.push(a);
                                if (b) ordered.push(b);
                              }
                            }
                            return ordered.map((item, index) => (
                            <div key={index} className={`interview-question-item ${item.sender === 'ai' ? 'response' : 'question'}`}>
                                <div className="interview-question-header">
                                <span className={`question-number-badge ${item.sender === 'ai' ? 'response-badge' : 'question-badge'}`}>
                                    {item.sender === 'ai' ? 'Response' : 'Question'}
                                </span>
                                <span className="timestamp">{item.timestamp}</span>
                                </div>
                                <div className="interview-question-text">
                                {item.message}
                                </div>
                            </div>
                            ));
                        })()}
                        </div>
                    ) : (
                        <div className="no-details">No conversation available</div>
                    )}
                    </div>
                )}
                </div>
            );
            })}
        </div>
        )}
    </div>
    );

  const renderResumesBuilt = () => (
    <div className="history-section">
      <div className="section-header">
        <h2>Resume Builder History ({resumeHistory.length})</h2>
        <button className="action-btn" onClick={() => navigate('/resume-builder')}>
          Build New Resume
        </button>
      </div>
      
      {resumeHistory.length === 0 ? (
        <div className="empty-state glass">
          <div className="empty-icon">📄</div>
          <h3>No Resumes Built Yet</h3>
          <p>Create your first professional resume using our AI-powered builder.</p>
          <button className="cta-button" onClick={() => navigate('/resume-builder')}>
            Build Resume
          </button>
        </div>
      ) : (
        <div className="quiz-history-list">
          {resumeHistory.map((resume) => {
            let displayName = resume.file_name;
            try {
              const resumeData = typeof resume.resume_data === 'string' 
                ? JSON.parse(resume.resume_data) 
                : resume.resume_data;
              
              const name = resumeData?.personalInfo?.name || 
                          resumeData?.personalInfo?.fullName || 
                          resumeData?.fullName || 
                          resumeData?.name;
              
              if (name && name.trim()) {
                displayName = `${name} - Resume`;
              }
            } catch (error) {
              console.error('Error parsing resume data for display:', error);
            }

            const rOpen = expandedResumes.has(resume.id);
            return (
              <div key={resume.id} className="quiz-history-item glass">
                <div className="quiz-header">
                  <div className="quiz-header-left" onClick={() => toggleResume(resume.id)} style={{ cursor: 'pointer' }}>
                    <div className="quiz-info">
                      <div className="quiz-card-top">
                        <h4 className="quiz-card-title">{displayName}</h4>
                      </div>
                      <div className="quiz-meta">
                        <span className="meta-item">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          {new Date(resume.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="quiz-header-right">
                    <button
                      className="pdf-download-btn-improved"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await fetch(`${API_BASE_URL}/api/resume/history/${resume.id}`, {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            
                            let resumeData = data.resume.resume_data;
                            if (typeof resumeData === 'string') {
                              resumeData = JSON.parse(resumeData);
                            }
                            
                            const blob = await templateService.generateFromTemplate(
                              data.resume.template_id, 
                              resumeData
                            );
                            
                            const fileName = data.resume.file_name || 
                                           `${resumeData?.personalInfo?.name || resumeData?.fullName || 'Resume'}_ATS_Optimized.docx`;
                            saveAs(blob, fileName);
                          } else {
                            alert('Failed to download resume. Please try again.');
                          }
                        } catch (error) {
                          console.error('Error downloading resume:', error);
                          alert('Error downloading resume. Please try again.');
                        }
                      }}
                      title="Download Resume"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      <span>Download</span>
                    </button>
                    <span className="expand-icon" onClick={() => toggleResume(resume.id)}>{rOpen ? '−' : '+'}</span>
                  </div>
                </div>
                {rOpen && (
                  <div className="quiz-details">
                    {loadingResumeDetails[resume.id] ? (
                      <div className="loading-details">Loading resume...</div>
                    ) : resumeDetails[resume.id] ? (
                      <div className="interview-questions-review">
                        {(() => {
                          const rd = resumeDetails[resume.id];
                          const info = rd?.personalInfo || {};
                          const exp = rd?.workExperience || rd?.experience || [];
                          const edu = rd?.education || [];
                          const skills = rd?.skills || [];
                          return (
                            <>
                              {info.email || info.phone ? (
                                <div className="interview-question-item">
                                  <div className="interview-question-header"><span className="question-number-badge">Contact</span></div>
                                  <div className="interview-question-text">{[info.email, info.phone, info.location].filter(Boolean).join(' · ')}</div>
                                </div>
                              ) : null}
                              {exp.length > 0 && (
                                <div className="interview-question-item">
                                  <div className="interview-question-header"><span className="question-number-badge">Experience</span></div>
                                  <div className="interview-question-text">
                                    {exp.map((e, i) => <div key={i}>{[e.position || e.title, e.company, e.duration || e.dates].filter(Boolean).join(' · ')}</div>)}
                                  </div>
                                </div>
                              )}
                              {edu.length > 0 && (
                                <div className="interview-question-item">
                                  <div className="interview-question-header"><span className="question-number-badge">Education</span></div>
                                  <div className="interview-question-text">
                                    {edu.map((e, i) => <div key={i}>{[e.degree, e.institution || e.school, e.year || e.dates].filter(Boolean).join(' · ')}</div>)}
                                  </div>
                                </div>
                              )}
                              {skills.length > 0 && (
                                <div className="interview-question-item">
                                  <div className="interview-question-header"><span className="question-number-badge">Skills</span></div>
                                  <div className="interview-question-text">{Array.isArray(skills) ? skills.join(', ') : skills}</div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="no-details">No details available</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderCoverLetters = () => (
    <div className="history-section">
      <div className="section-header">
        <h2>Cover Letter History ({coverLetterHistory.length})</h2>
        <button className="action-btn" onClick={() => navigate('/CoverLetter')}>
          Create New Cover Letter
        </button>
      </div>
      
      {coverLetterHistory.length === 0 ? (
        <div className="empty-state glass">
          <div className="empty-icon">🛡️</div>
          <h3>No Cover Letters Yet</h3>
          <p>Create personalized cover letters tailored to specific job descriptions.</p>
          <button className="cta-button" onClick={() => navigate('/CoverLetter')}>
            Create Cover Letter
          </button>
        </div>
      ) : (
        <div className="quiz-history-list">
          {coverLetterHistory.map((coverLetter) => {
            let displayName = coverLetter.file_name || 'Cover Letter';
            
            if (coverLetter.job_title && coverLetter.company_name) {
              displayName = `${coverLetter.job_title} at ${coverLetter.company_name}`;
            } else if (coverLetter.job_title) {
              displayName = `Cover Letter - ${coverLetter.job_title}`;
            } else if (coverLetter.company_name) {
              displayName = `Cover Letter - ${coverLetter.company_name}`;
            }

            const clOpen = expandedCoverLetters.has(coverLetter.id);
            return (
              <div key={coverLetter.id} className="quiz-history-item glass">
                <div className="quiz-header">
                  <div className="quiz-header-left" onClick={() => toggleCoverLetter(coverLetter.id)} style={{ cursor: 'pointer' }}>
                    <div className="quiz-info">
                      <div className="quiz-card-top">
                        <h4 className="quiz-card-title">{displayName}</h4>
                      </div>
                      <div className="quiz-meta">
                        <span className="meta-item">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                          {new Date(coverLetter.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="quiz-header-right">
                    <button
                      className="pdf-download-btn-improved"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const detail = coverLetterDetails[coverLetter.id];
                          if (detail) { await handleDownloadCoverLetter(detail); return; }
                          const response = await fetch(`${API_BASE_URL}/api/cover-letter/history/${coverLetter.id}`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            await handleDownloadCoverLetter(data.coverLetter);
                          }
                        } catch (error) { console.error('Error downloading cover letter:', error); }
                      }}
                      title="Download Cover Letter"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      <span>Download</span>
                    </button>
                    <span className="expand-icon" onClick={() => toggleCoverLetter(coverLetter.id)}>{clOpen ? '−' : '+'}</span>
                  </div>
                </div>
                {clOpen && (
                  <div className="quiz-details">
                    {loadingCoverLetterDetails[coverLetter.id] ? (
                      <div className="loading-details">Loading cover letter...</div>
                    ) : coverLetterDetails[coverLetter.id] ? (
                      <div className="interview-question-item" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                        {coverLetterDetails[coverLetter.id].cover_letter_content}
                      </div>
                    ) : (
                      <div className="no-details">No content available</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderInterviewQuestions = () => (
    <div className="history-section">
      <div className="section-header">
        <h2>Interview Questions History ({interviewQuestionsHistory.length})</h2>
        <button className="action-btn" onClick={() => navigate('/InterviewQuestions')}>
          Generate New Questions
        </button>
      </div>
      
      {interviewQuestionsHistory.length === 0 ? (
        <div className="empty-state glass">
          <div className="empty-icon">💎</div>
          <h3>No Interview Questions Generated Yet</h3>
          <p>Generate personalized interview questions and answers based on your resume and job description.</p>
          <button className="cta-button" onClick={() => navigate('/InterviewQuestions')}>
            Generate Questions
          </button>
        </div>
      ) : (
        <div className="quiz-history-list">
          {interviewQuestionsHistory.map((session) => (
            <div key={session.id} className="quiz-history-item glass">
              <div className="quiz-header">
                <div className="quiz-header-left" onClick={() => toggleInterviewSession(session.id)}>
                  <div className="quiz-info">
                    <h4>
                      {session.company_name}: {session.job_title}
                      <span className="meta-item">  ({new Date(session.created_at).toLocaleDateString()}) </span>
                    </h4>
                  </div>
                </div>
                <div className="quiz-header-right">
                  <button 
                    className="pdf-download-btn-improved"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!session.detailsLoaded) {
                        const updatedSession = await fetchInterviewSessionDetails(session.id);
                        if (updatedSession && updatedSession.questions_data) {
                          handleGenerateInterviewQuestionsPDF(updatedSession);
                        }
                      } else {
                        handleGenerateInterviewQuestionsPDF(session);
                      }
                    }}
                    title="Download PDF"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Download PDF</span>
                  </button>
                  <span className="expand-icon" onClick={() => toggleInterviewSession(session.id)}>
                    {expandedInterviewSessions.has(session.id) ? '−' : '+'}
                  </span>
                </div>
              </div>
              
              {expandedInterviewSessions.has(session.id) && (
                <div className="quiz-details">
                  {loadingSessionDetails[session.id] ? (
                    <div className="loading-details">Loading questions...</div>
                  ) : session.questions_data ? (
                    <div className="interview-questions-review">
                      {(() => {
                        const questions = typeof session.questions_data === 'string' 
                          ? JSON.parse(session.questions_data) 
                          : session.questions_data;
                        
                        return questions.map((question, index) => (
                          <div key={index} className="interview-question-item">
                            <div className="interview-question-header">
                              <span className="question-number-badge">Q{index + 1}</span>
                            </div>
                            <div className="interview-question-text">{question.question}</div>
                            <div className="interview-answer-section">
                              <strong>Answer:</strong>
                              <p>{question.answer}</p>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className="no-details">No questions available</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const fetchMockInterviewSessionDetails = async (sessionId) => {
    setLoadingMockInterviewDetails(prev => ({ ...prev, [sessionId]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/mock-interview/history/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const updated = { ...mockInterviewHistory.find(s => s.id === sessionId), ...data.session, detailsLoaded: true };
        setMockInterviewHistory(prev => prev.map(s => s.id === sessionId ? updated : s));
        return updated;
      }
    } catch (error) {
      console.error('Error fetching mock interview session details:', error);
    } finally {
      setLoadingMockInterviewDetails(prev => ({ ...prev, [sessionId]: false }));
    }
    return null;
  };

  const toggleCoverLetter = async (id) => {
    const next = new Set(expandedCoverLetters);
    if (next.has(id)) { next.delete(id); setExpandedCoverLetters(next); return; }
    next.add(id);
    setExpandedCoverLetters(next);
    if (!coverLetterDetails[id]) {
      setLoadingCoverLetterDetails(prev => ({ ...prev, [id]: true }));
      try {
        const res = await fetch(`${API_BASE_URL}/api/cover-letter/history/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCoverLetterDetails(prev => ({ ...prev, [id]: data.coverLetter }));
        }
      } catch (e) { console.error(e); }
      finally { setLoadingCoverLetterDetails(prev => ({ ...prev, [id]: false })); }
    }
  };

  const toggleResume = async (id) => {
    const next = new Set(expandedResumes);
    if (next.has(id)) { next.delete(id); setExpandedResumes(next); return; }
    next.add(id);
    setExpandedResumes(next);
    if (!resumeDetails[id]) {
      setLoadingResumeDetails(prev => ({ ...prev, [id]: true }));
      try {
        const res = await fetch(`${API_BASE_URL}/api/resume/history/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          const rd = typeof data.resume.resume_data === 'string' ? JSON.parse(data.resume.resume_data) : data.resume.resume_data;
          setResumeDetails(prev => ({ ...prev, [id]: rd }));
        }
      } catch (e) { console.error(e); }
      finally { setLoadingResumeDetails(prev => ({ ...prev, [id]: false })); }
    }
  };

  const toggleMockInterviewSession = async (sessionId) => {
    const newExpanded = new Set(expandedMockInterviews);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
      const session = mockInterviewHistory.find(s => s.id === sessionId);
      if (!session.detailsLoaded) await fetchMockInterviewSessionDetails(sessionId);
    }
    setExpandedMockInterviews(newExpanded);
  };

  const handleMockInterviewPDF = (session) => {
    const questions = session.questions_data || [];
    const evaluation = session.answers_evaluation || {};
    const questionScores = evaluation.questionScores || [];
    const generalFeedback = evaluation.generalFeedback || '';
    if (!questions.length) return;

    const doc = createDoc();
    const { W, M, CW } = PDF;
    const dateStr = new Date(session.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const levelMap = { entry: 'Entry Level', mid: 'Mid Level', senior: 'Senior Level', lead: 'Lead / Principal', executive: 'Executive' };
    const levelLabel = levelMap[session.experience_level] || session.experience_level || '';
    const avgConf = questionScores.length
      ? Math.round(questionScores.reduce((s, q) => s + (q.confidenceScore || 0), 0) / questionScores.length)
      : 0;

    fR(doc, 255, 255, 255, 0, 0, W, PDF.H);
    drawCoverHeader(doc, { reportTitle: 'Mock Interview Report', role: session.job_role, company: session.company || '', levelLabel, dateStr });

    let y = 66;
    const cardW = (CW - 8) / 3;
    const scoreColor = session.overall_score >= 75 ? [34, 197, 94] : session.overall_score >= 50 ? [234, 179, 8] : [239, 68, 68];
    const confColor = avgConf >= 70 ? [34, 197, 94] : avgConf >= 45 ? [234, 179, 8] : [239, 68, 68];
    [[session.overall_score, 'Readiness Score', scoreColor], [avgConf, 'Avg Confidence', confColor], [questions.length, 'Questions', [99, 102, 241]]].forEach(([val, label, col], i) => {
      const cx = M + i * (cardW + 4);
      fR(doc, col[0], col[1], col[2], cx, y, cardW, 4);
      fR(doc, 246, 244, 255, cx, y + 4, cardW, 28);
      sR(doc, 218, 213, 238, 0.3, cx, y + 4, cardW, 28);
      T(doc, col, 22, true, String(val), cx + cardW / 2, y + 21, { align: 'center' });
      T(doc, [80, 70, 110], 7.5, false, label, cx + cardW / 2, y + 29, { align: 'center' });
    });
    y += 44;

    if (generalFeedback) {
      y = drawSectionHeading(doc, 'General Feedback', y);
      const fbLines = doc.splitTextToSize(generalFeedback, CW - 14);
      const fbH = fbLines.length * 5.5 + 10;
      fR(doc, 246, 244, 255, M, y, CW, fbH);
      sR(doc, 218, 213, 238, 0.3, M, y, CW, fbH);
      fR(doc, 109, 40, 217, M, y, 3.5, fbH);
      let ty = y + 8;
      fbLines.forEach(line => { T(doc, [35, 28, 55], 8.5, false, line, M + 7, ty); ty += 5.5; });
      y += fbH + 8;
    }

    questions.forEach((questionText, idx) => {
      const ev = questionScores[idx] || {};
      if (y + 60 > PDF.H - 20) { doc.addPage(); fR(doc, 255, 255, 255, 0, 0, W, PDF.H); drawPageHeader(doc, session.job_role); y = 22; }

      fR(doc, 20, 14, 50, M, y, CW, 10);
      T(doc, [255, 255, 255], 8, true, `Q${idx + 1}`, M + 3, y + 7);
      const qLines = doc.splitTextToSize(String(questionText?.question || questionText), CW - 20);
      T(doc, [196, 181, 253], 7.5, false, qLines[0] || '', M + 14, y + 7);
      y += 12;

      const mW = (CW - 6) / 4;
      const mData = [
        ['Score', `${ev.score || 0}/100`, ev.score >= 70 ? [34,197,94] : ev.score >= 50 ? [234,179,8] : [239,68,68]],
        ['Confidence', `${ev.confidenceScore || 0}%`, (ev.confidenceScore||0) >= 70 ? [34,197,94] : (ev.confidenceScore||0) >= 45 ? [234,179,8] : [239,68,68]],
        ['Words', String(ev.wordCount || 0), [99, 102, 241]],
        ['Pace', ev.pace ? `${ev.pace} WPM` : 'N/A', ev.pace >= 110 && ev.pace <= 160 ? [34,197,94] : [234,179,8]],
      ];
      mData.forEach(([label, val, col], i) => {
        const mx = M + i * (mW + 2);
        fR(doc, col[0], col[1], col[2], mx, y, mW, 2.5);
        fR(doc, 250, 248, 255, mx, y + 2.5, mW, 14);
        sR(doc, 218, 213, 238, 0.3, mx, y + 2.5, mW, 14);
        T(doc, col, 10, true, val, mx + mW / 2, y + 11, { align: 'center' });
        T(doc, [100, 90, 130], 6.5, false, label, mx + mW / 2, y + 15, { align: 'center' });
      });
      y += 20;

      if (ev.advice) {
        const advLines = doc.splitTextToSize(ev.advice, CW - 10);
        const advH = advLines.length * 5 + 10;
        if (y + advH > PDF.H - 20) { doc.addPage(); fR(doc, 255, 255, 255, 0, 0, W, PDF.H); drawPageHeader(doc, session.job_role); y = 22; }
        fR(doc, 255, 251, 235, M, y, CW, advH);
        sR(doc, 253, 211, 77, 0.4, M, y, CW, advH);
        fR(doc, 245, 158, 11, M, y, 3, advH);
        T(doc, [146, 64, 14], 7, true, 'Improvement Advice', M + 6, y + 6);
        let advy = y + 11;
        advLines.forEach(line => { T(doc, [120, 53, 15], 7.5, false, line, M + 6, advy); advy += 5; });
        y += advH + 8;
      }
    });

    drawFooters(doc);
    const safeName = (session.job_role || 'Mock_Interview').replace(/[^a-z0-9]/gi, '_');
    doc.save(`TERVIEW_Mock_Interview_${safeName}.pdf`);
  };

  const renderMockInterviews = () => (
    <div className="history-section">
      <div className="section-header">
        <h2>Mock Interview History ({mockInterviewHistory.length})</h2>
        <button className="action-btn" onClick={() => navigate('/MockInterview')}>
          Start New Mock Interview
        </button>
      </div>
      {mockInterviewHistory.length === 0 ? (
        <div className="empty-state glass">
          <div className="empty-icon">🎤</div>
          <h3>No Mock Interviews Yet</h3>
          <p>Practice with AI-powered mock interviews and get evaluated on your answers.</p>
          <button className="cta-button" onClick={() => navigate('/MockInterview')}>
            Start Mock Interview
          </button>
        </div>
      ) : (
        <div className="quiz-history-list">
          {mockInterviewHistory.map((session) => {
            const rawDuration = session.duration_seconds || 0;
            const validDuration = rawDuration > 0 && rawDuration <= 3600;
            const durationMin = Math.floor(rawDuration / 60);
            const durationSec = rawDuration % 60;
            const durationLabel = validDuration ? `${durationMin}:${String(durationSec).padStart(2, '0')}` : '—';
            const scoreColor = session.overall_score >= 75 ? '#4ade80' : session.overall_score >= 50 ? '#fbbf24' : '#f87171';
            const scoreBg = session.overall_score >= 75 ? 'rgba(74,222,128,0.15)' : session.overall_score >= 50 ? 'rgba(251,191,36,0.15)' : 'rgba(248,113,113,0.15)';
            const isOpen = expandedMockInterviews.has(session.id);
            return (
              <div key={session.id} className="quiz-history-item glass">
                <div className="quiz-header">
                  <div className="quiz-header-left" onClick={() => toggleMockInterviewSession(session.id)} style={{ cursor: 'pointer' }}>
                    <div className="quiz-info">
                      <h4>
                        {session.job_role}{session.company ? ` at ${session.company}` : ''}
                        <span style={{ marginLeft: 10, padding: '2px 10px', borderRadius: 6, fontSize: 13, background: scoreBg, color: scoreColor, fontWeight: 700 }}>
                          {session.overall_score}%
                        </span>
                      </h4>
                      <div className="quiz-meta">
                        <span className="meta-item">{session.experience_level} Level</span>
                        <span className="meta-separator">•</span>
                        <span className="meta-item">{durationLabel}</span>
                        <span className="meta-separator">•</span>
                        <span className="meta-item">{new Date(session.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="quiz-header-right">
                    <button
                      className="pdf-download-btn-improved"
                      onClick={async (e) => {
                        e.stopPropagation();
                        let s = session;
                        if (!s.detailsLoaded) s = await fetchMockInterviewSessionDetails(session.id) || session;
                        if (s.questions_data) handleMockInterviewPDF(s);
                      }}
                      title="Download PDF"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      <span>Download PDF</span>
                    </button>
                    <span className="expand-icon" onClick={() => toggleMockInterviewSession(session.id)}>{isOpen ? '−' : '+'}</span>
                  </div>
                </div>
                {isOpen && (
                  <div className="quiz-details">
                    {loadingMockInterviewDetails[session.id] ? (
                      <div className="loading-details">Loading session...</div>
                    ) : session.questions_data ? (
                      <div className="interview-questions-review">
                        {(() => {
                          const evaluation = session.answers_evaluation || {};
                          const questionScores = evaluation.questionScores || [];
                          const generalFeedback = evaluation.generalFeedback || '';
                          return (
                            <>
                              {generalFeedback && (
                                <div className="interview-question-item" style={{ background: 'rgba(109,40,217,0.07)', borderLeft: '3px solid #7c3aed', marginBottom: 12 }}>
                                  <div className="interview-question-header">
                                    <span className="question-number-badge" style={{ background: 'rgba(109,40,217,0.2)', color: '#a78bfa' }}>General Feedback</span>
                                  </div>
                                  <div className="interview-question-text">{generalFeedback}</div>
                                </div>
                              )}
                              {session.questions_data.map((questionText, idx) => {
                                const ev = questionScores[idx] || {};
                                const sc = ev.score || 0;
                                // Backend scores each question 0–100 (contentScore 0–50 + authenticityScore 0–50).
                                const scColor = sc >= 70 ? '#4ade80' : sc >= 50 ? '#fbbf24' : '#f87171';
                                return (
                                  <div key={idx} className="interview-question-item">
                                    <div className="interview-question-header">
                                      <span className="question-number-badge">Q{idx + 1}</span>
                                      <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 5, fontSize: 12, background: `${scColor}22`, color: scColor, fontWeight: 700 }}>
                                        {sc}/100
                                      </span>
                                      {ev.confidenceScore != null && (
                                        <span style={{ marginLeft: 6, fontSize: 12, color: '#a78bfa' }}>· {ev.confidenceScore}% confidence</span>
                                      )}
                                    </div>
                                    <div className="interview-question-text">{questionText?.question || String(questionText)}</div>
                                    {ev.advice && (
                                      <div className="interview-answer-section">
                                        <strong>Advice:</strong>
                                        <p>{ev.advice}</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="no-details">No session details available</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch(activeTab) {
      case 'overview':
        return renderOverview();
      case 'interviews':
        return renderInterviews();
      case 'resumes-built':
        return renderResumesBuilt();
      case 'cover-letters':
        return renderCoverLetters();
      case 'interview-questions':
        return renderInterviewQuestions();
      case 'auto-apply-sessions':
        return renderAutoApplySessions();
      case 'all-applied-jobs':
        return renderAllAppliedJobs();
      case 'mock-interviews':
        return renderMockInterviews();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background"></div>
      
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <div className="dashboard-container">
        <Header setShowAuthModal={setShowAuthModal} />
        <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

        <main className="dashboard-main">
          <div className="dashboard-header">
            <h1>Welcome back, {user.name}!</h1>
            <p>Track your progress and access your AI-powered career tools</p>
          </div>

          <div className="dashboard-content">
            <aside className="dashboard-sidebar glass">
              <nav className="dashboard-nav">
                <button 
                  className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  <span className="nav-icon">📊</span>
                  Overview
                </button>
                
                <button 
                  className={`nav-item ${activeTab === 'interviews' ? 'active' : ''}`}
                  onClick={() => setActiveTab('interviews')}
                >
                  <span className="nav-icon">🎯</span>
                  Interview Assistant
                </button>
                
                <button 
                  className={`nav-item ${activeTab === 'resumes-built' ? 'active' : ''}`}
                  onClick={() => setActiveTab('resumes-built')}
                >
                  <span className="nav-icon">📄</span>
                  Resume Builder
                </button>
                
                <button 
                  className={`nav-item ${activeTab === 'cover-letters' ? 'active' : ''}`}
                  onClick={() => setActiveTab('cover-letters')}
                >
                  <span className="nav-icon">🛡️</span>
                  Cover Letter
                </button>
                
                <button 
                  className={`nav-item ${activeTab === 'interview-questions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('interview-questions')}
                >
                  <span className="nav-icon">💎</span>
                  Interview Questions
                </button>
                
                <button
                  className={`nav-item ${activeTab === 'auto-apply-sessions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('auto-apply-sessions')}
                >
                  <span className="nav-icon">🚀</span>
                  Auto Apply Sessions
                </button>

                <button
                  className={`nav-item ${activeTab === 'all-applied-jobs' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all-applied-jobs')}
                >
                  <span className="nav-icon">💼</span>
                  All Applied Jobs
                </button>

                <button
                  className={`nav-item ${activeTab === 'mock-interviews' ? 'active' : ''}`}
                  onClick={() => setActiveTab('mock-interviews')}
                >
                  <span className="nav-icon">🎤</span>
                  Mock Interviews
                </button>
              </nav>
            </aside>

            <div className="dashboard-body">
              {renderContent()}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default Dashboard;